/**
 * Review an AI-written message before it goes out.
 *
 * Nothing is sent from here — the buttons hand off to the user's mail client
 * or WhatsApp, which keeps the workspace out of the business of delivering
 * mail while still logging that the outreach happened.
 */
import { useState, useEffect } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import { digitsOf, whatsappLink } from '../../lib/format.js';
import { formatDate } from '../../lib/dates.js';
import { companyName, contactOf } from '../../domain/crm.js';
import Sheet, { Field } from '../Sheet.jsx';
import { Spinner, AIBadge } from '../Primitives.jsx';

export default function DraftModal() {
  const {
    S, rows, crm, draft, closeDraft, openDraft, patchClient, noteActivity, toast
  } = useApp();

  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const isDeal = draft?.kind === 'deal';
  const client = !isDeal ? rows.find(r => r.id === draft?.id) : null;
  const deal = isDeal ? crm.deals.find(d => d.id === draft?.id) : null;
  const contact = isDeal ? (contactOf(crm, deal?.contact) || {}) : null;

  /* Refill the fields whenever a new draft arrives. */
  useEffect(() => {
    if (!draft || draft.loading) return;
    setSubject(draft.subject || '');
    setBody(draft.body || '');
    setTo(isDeal
      ? (draft.channel === 'whatsapp' ? (contact?.phone || '') : (contact?.email || ''))
      : (client?.email || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.id, draft?.loading, draft?.channel]);

  if (!draft) return null;

  const title = isDeal
    ? `Draft for ${deal?.title || 'deal'}`
    : `Draft for ${client?.name || 'client'}`;

  const recipient = isDeal
    ? (contact?.name || companyName(crm, deal?.cid) || 'them')
    : (client?.contact || client?.name || 'the client');

  /** Record that we reached out, on whichever record this draft belongs to. */
  const logSent = channel => {
    if (isDeal) noteActivity(draft.id, 'Outreach sent', channel);
    else patchClient(draft.id, { lastContacted: formatDate(new Date(), false, S.dateFmt), outbox: undefined });
    closeDraft();
  };

  const copy = () => {
    const text = (draft.channel === 'email' && subject ? `Subject: ${subject}\n\n` : '') + body;
    if (!navigator.clipboard) { toast('Clipboard unavailable'); return; }
    navigator.clipboard.writeText(text).then(() => toast('Copied'), () => toast('Copy failed'));
  };

  /*
   * A mailto: link is handed to whatever the operating system has registered
   * as the default mail program - on Windows that is usually Outlook, and a
   * web page cannot override it. Gmail is a website, so it needs its own
   * compose URL rather than the mail protocol.
   */
  const sendMail = () => {
    window.open(
      `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
      '_blank'
    );
    logSent('Email');
  };

  const sendGmail = () => {
    window.open(
      'https://mail.google.com/mail/?view=cm&fs=1'
      + `&to=${encodeURIComponent(to)}`
      + `&su=${encodeURIComponent(subject)}`
      + `&body=${encodeURIComponent(body)}`,
      '_blank', 'noopener'
    );
    logSent('Email');
  };

  const sendWhatsApp = () => {
    if (!digitsOf(to)) { toast('No phone number'); return; }
    window.open(whatsappLink(to, body), '_blank');
    logSent('WhatsApp');
  };

  return (
    <Sheet
      icon="zap"
      tone="g-pink"
      title={title}
      badge={<AIBadge />}
      onClose={closeDraft}
      footerLeft={draft.loading ? null : (
        <>
          <button type="button" className="btn" onClick={() => openDraft({ kind: draft.kind, id: draft.id, channel: draft.channel })}>
            Rewrite
          </button>
          <button type="button" className="btn" onClick={copy}>Copy</button>
          <button type="button" className="btn" onClick={sendWhatsApp}>WhatsApp</button>
          <button type="button" className="btn" onClick={sendMail}>Mail app</button>
          <button type="button" className="btn primary" onClick={sendGmail}>Gmail</button>
        </>
      )}
    >
      {draft.loading ? (
        <div className="quiet" style={{ gridColumn: '1/-1' }}>
          <Spinner /> Writing to {recipient}…
        </div>
      ) : (
        <>
          <Field label="To" wide>
            <input value={to} onChange={e => setTo(e.target.value)} />
          </Field>
          {draft.channel === 'email' ? (
            <Field label="Subject" wide>
              <input value={subject} onChange={e => setSubject(e.target.value)} />
            </Field>
          ) : null}
          <Field label="Message" wide>
            <textarea style={{ minHeight: 230 }} value={body} onChange={e => setBody(e.target.value)} />
          </Field>
        </>
      )}
    </Sheet>
  );
}
